import React, { useEffect, useState } from 'react';
import {
  Send, Plus, X, Loader, CheckCircle, AlertCircle, MessageCircle,
  Facebook, Clock, RefreshCw, Ban, Calendar
} from 'lucide-react';
import {
  getPosts, createPost, publishPostNow, cancelPost, getMessageTemplates
} from '../services/api';
import './PublishingQueue.css';

const STATUS = {
  DRAFT:      { label: 'Brouillon',  color: '#475569', bg: '#f1f5f9' },
  SCHEDULED:  { label: 'Planifié',   color: '#2563eb', bg: '#dbeafe' },
  PUBLISHING: { label: 'En cours',   color: '#d97706', bg: '#fef3c7' },
  DONE:       { label: 'Publié',     color: '#16a34a', bg: '#dcfce7' },
  FAILED:     { label: 'Échec',      color: '#dc2626', bg: '#fee2e2' },
};

const CHANNELS = {
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: '#25D366' },
  facebook: { label: 'Facebook', icon: Facebook, color: '#1877F2' },
};

const EMPTY = { channel: 'whatsapp', text: '', target: '', locale: 'fr', mediaUrl: '', scheduledAt: '' };

const StatusBadge = ({ s }) => {
  const st = STATUS[s] || STATUS.DRAFT;
  return <span className="pq-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>;
};

const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export default function PublishingQueue() {
  const [posts, setPosts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await getPosts(params);
      setPosts(res.data.data || []);
    } catch { showToast('error', 'Erreur de chargement de la file'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getMessageTemplates().then((r) => setTemplates(r.data.data || [])).catch(() => {}); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        channel: form.channel,
        content: { text: form.text, locale: form.locale, mediaUrl: form.mediaUrl || null },
        target: form.target || null,
      };
      if (form.scheduledAt) payload.scheduledAt = new Date(form.scheduledAt).toISOString();
      await createPost(payload);
      showToast('success', form.scheduledAt ? 'Post planifié' : 'Brouillon créé');
      setShowModal(false); setForm(EMPTY); load();
    } catch (err) {
      showToast('error', err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const doPublish = async (id) => {
    try { await publishPostNow(id); showToast('success', 'Publication lancée'); load(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Échec de publication'); load(); }
  };
  const doCancel = async (id) => {
    if (!window.confirm('Annuler ce post planifié ?')) return;
    try { await cancelPost(id); showToast('success', 'Post annulé'); load(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  return (
    <div className="pq-page">
      {toast && (
        <div className={`pq-toast pq-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{toast.text}</span>
        </div>
      )}

      <div className="pq-header">
        <div>
          <h1 className="pq-title"><Send size={26} /> Publications</h1>
          <p className="pq-subtitle">File de diffusion WhatsApp &amp; Facebook — planification et suivi des statuts.</p>
        </div>
        <div className="pq-header-actions">
          <button className="pq-btn pq-btn--ghost" onClick={load} title="Rafraîchir"><RefreshCw size={16} /></button>
          <button className="pq-btn pq-btn--primary" onClick={() => setShowModal(true)}><Plus size={18} /> Nouveau post</button>
        </div>
      </div>

      <div className="pq-filters">
        {['all', 'DRAFT', 'SCHEDULED', 'PUBLISHING', 'DONE', 'FAILED'].map((f) => (
          <button key={f} className={`pq-chip ${filter === f ? 'pq-chip--active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'Tout' : (STATUS[f]?.label || f)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="pq-loading"><Loader className="pq-spin" size={28} /> Chargement…</div>
      ) : posts.length === 0 ? (
        <div className="pq-empty"><Send size={42} /><p>Aucun post dans la file.</p></div>
      ) : (
        <div className="pq-table-wrap">
          <table className="pq-table">
            <thead>
              <tr><th>Canal</th><th>Message</th><th>Statut</th><th>Planifié</th><th>Tentatives</th><th></th></tr>
            </thead>
            <tbody>
              {posts.map((p) => {
                const ch = CHANNELS[p.channel] || {};
                const CIcon = ch.icon || MessageCircle;
                return (
                  <tr key={p.id}>
                    <td><span className="pq-channel"><CIcon size={16} style={{ color: ch.color }} /> {ch.label}</span></td>
                    <td className="pq-msg">
                      <div className="pq-msg-text">{p.content?.text || <em>(média)</em>}</div>
                      {p.target && <div className="pq-msg-target">→ {p.target}</div>}
                      {p.lastError && p.status === 'FAILED' && <div className="pq-msg-err">{p.lastError}</div>}
                    </td>
                    <td><StatusBadge s={p.status} /></td>
                    <td className="pq-date"><Clock size={13} /> {fmtDate(p.scheduledAt)}</td>
                    <td className="pq-attempts">{p.attempts}/{p.maxAttempts}</td>
                    <td className="pq-actions">
                      {['DRAFT', 'FAILED'].includes(p.status) && (
                        <button className="pq-act pq-act--go" onClick={() => doPublish(p.id)} title="Publier maintenant"><Send size={14} /></button>
                      )}
                      {p.status === 'SCHEDULED' && (
                        <button className="pq-act pq-act--cancel" onClick={() => doCancel(p.id)} title="Annuler"><Ban size={14} /></button>
                      )}
                      {p.externalId && <span className="pq-ext" title="ID externe">{p.externalId.slice(0, 14)}…</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="pq-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pq-modal-head">
              <h3><Send size={20} /> Nouveau post</h3>
              <button className="pq-icon-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="pq-form">
              <label>Canal</label>
              <div className="pq-channel-pick">
                {Object.entries(CHANNELS).map(([key, ch]) => {
                  const CIcon = ch.icon;
                  return (
                    <button type="button" key={key}
                      className={`pq-chpick ${form.channel === key ? 'pq-chpick--sel' : ''}`}
                      onClick={() => setForm({ ...form, channel: key })}>
                      <CIcon size={18} style={{ color: ch.color }} /> {ch.label}
                    </button>
                  );
                })}
              </div>

              <label>Message *
                <textarea rows={3} required value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="🛍️ Promo du jour…" />
              </label>

              {templates.length > 0 && (
                <div className="pq-templates">
                  <span>Insérer un modèle :</span>
                  {templates.map((t) => (
                    <button type="button" key={t.id} className="pq-tpl"
                      onClick={() => setForm({ ...form, text: t.bodies?.[form.locale] || t.bodies?.fr || '' })}>
                      {t.nom}
                    </button>
                  ))}
                </div>
              )}

              <div className="pq-form-row">
                <label>{form.channel === 'whatsapp' ? 'Destinataire (n°)' : 'Page (ID, option.)'}
                  <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}
                    placeholder={form.channel === 'whatsapp' ? '+221 77…' : '(défaut env)'} />
                </label>
                <label>Langue
                  <select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
                    <option value="fr">Français</option>
                    <option value="wo">Wolof</option>
                  </select>
                </label>
              </div>

              <label>Média (URL, optionnel)
                <input value={form.mediaUrl} onChange={(e) => setForm({ ...form, mediaUrl: e.target.value })}
                  placeholder="/uploads/creatives/…" />
              </label>

              <label><Calendar size={14} style={{ verticalAlign: '-2px' }} /> Planifier (vide = brouillon)
                <input type="datetime-local" value={form.scheduledAt}
                  onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} />
              </label>

              <div className="pq-modal-foot">
                <button type="button" className="pq-btn pq-btn--ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="pq-btn pq-btn--primary" disabled={submitting}>
                  {submitting ? <Loader className="pq-spin" size={16} /> : <Plus size={16} />}
                  {form.scheduledAt ? ' Planifier' : ' Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
